/**
 * The Current Set, grouped by discipline (ADR-0061 D5 — "sheet-first viewing").
 *
 * ## Why this is OURS to render
 *
 * D5 exists because Nexus confirmed they have no PDF viewer for us to consume, so first paint is
 * GGPathOS's. This is the minimal honest version of it: the set becomes VISIBLE. Until now the drawing
 * record could be written and queried but never looked at, which is the failure mode this repo names
 * most often — built, and nothing reads it.
 *
 * ## What it deliberately does NOT do
 *
 * 🔴 **It links or views a sheet ONLY when the record holds its document id** (migration 0052,
 * assigned at ingestion). A sheet without one is shown by the file name canon gave it, with the reason
 * it is not clickable: rendering a plausible-looking link that 404s, or guessing a document by
 * filename, would be re-deriving identity, which D2 forbids.
 *
 * The Current Set's links stay on `/download` — the CURRENT file — and say so. Version-pinned links
 * (D4) live on the set as filed below, through the as-filed route; ADR-0061 D4 keeps the Current Set
 * off it in the same change.
 *
 * ## The in-app viewer (ADR-0061 D6 — render; the version-pinned cite is the set as filed, D4)
 *
 * A matched sheet can be VIEWED inline, beneath its row. The pixels come from `/download` — the SAME
 * route the sheet's link cites — and the viewer names that route on screen. D6's rule is that one
 * route supplies both the pixels and the citation for a surface and that WHICH route is recorded;
 * Nexus's `/view` resolves without `/download`'s content-type tiebreak, so it can show different bytes
 * under the same document id. Every render is a retrieval and writes its own attributable ledger row.
 */

import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getCurrentSet,
  getFiledIssues,
  getSession,
  getSetAsFiled,
  issueMarks,
  type AsOfSheet,
  type CurrentSet,
  type DrawingSheet,
  type FiledIssue,
  type SetAsFiled,
} from '../api.js';
import {
  documentDownloadUrl,
  retrieveDocument,
  retrieveUrl,
  sheetAsFiledUrl,
} from '../retrieval.js';
import { signInPathFor } from '../return-path.js';
import { timestamp } from '../format.js';

/**
 * Group by the discipline prefix, preserving the order the api sent.
 *
 * The route already orders by `(discipline, sheet_number)`, so this only has to segment a sorted list
 * — it must never RE-SORT. The record's order is canon's; re-sorting here would be a second, quieter
 * opinion about the set.
 */
function groupByDiscipline<T extends { discipline: string }>(
  sheets: readonly T[],
): [string, T[]][] {
  const groups: [string, T[]][] = [];
  for (const sheet of sheets) {
    const key = sheet.discipline || '—';
    const last = groups[groups.length - 1];
    if (last && last[0] === key) last[1].push(sheet);
    else groups.push([key, [sheet]]);
  }
  return groups;
}

/**
 * The FETCHING wrapper. Split from the rendering below deliberately: this package's component tests are
 * SSR-only (`renderToStaticMarkup`, no jsdom), and `useEffect` does not run under SSR — so a component
 * that fetched and rendered in one piece would be testable in exactly one of its four states, the
 * loading one. The split is what makes "could not load" and "empty record" assertable at all.
 */
export function ProjectDrawings({ code }: { code: string }) {
  const [set, setSet] = useState<CurrentSet | null>(null);

  useEffect(() => {
    let live = true;
    void getCurrentSet(code).then((s) => {
      if (live) setSet(s);
    });
    return () => {
      live = false;
    };
  }, [code]);

  /**
   * Open one sheet. The tab is opened in this SYNCHRONOUS prologue — before any `await` — or the
   * browser blocks it as a popup; `retrieveDocument` then points it at the route once the session is
   * known good. Lifted verbatim from the documents panel rather than reinvented: a second way to
   * open a file that behaves differently is a way to open a file wrongly.
   */
  const navigate = useNavigate();
  const onRetrieve = (documentId: string) => {
    void (async () => {
      const outcome = await retrieveDocument(
        {
          openTab: () => window.open('', '_blank'),
          ensureSession: async () => (await getSession()) !== null,
          goTo: (url) => window.location.assign(url),
        },
        code,
        documentId,
      );
      /*
        🔴 THE OUTCOME IS NOT OPTIONAL. Discarding it left a signed-out reader with a blank tab that
        opened, closed, and returned them to the page with no sign-in and no explanation — the
        session had expired and nothing said so. Both other callers route to sign-in; this is the
        third, and a third way to fail is still a way to fail.
      */
      if (outcome === 'signed-out')
        void navigate(signInPathFor(window.location, window.location.origin), { replace: true });
    })();
  };

  /**
   * View one sheet inline. 🔴 THE SESSION IS RENEWED BEFORE THE FRAME EXISTS. The frame is a
   * navigation to the api, like a link — the SPA's XHR-side refresh never runs for it — so a lapsed
   * 15-minute cookie would put raw 401 JSON inside the viewer. Mounting the frame only after
   * `getSession()` succeeds is the same order `retrieveDocument` uses for a new tab.
   */
  const [viewing, setViewing] = useState<string | null>(null);
  const onView = (sheet: DrawingSheet) => {
    if (viewing === sheet.versionId) {
      setViewing(null);
      return;
    }
    void (async () => {
      if ((await getSession()) === null) {
        void navigate(signInPathFor(window.location, window.location.origin), { replace: true });
        return;
      }
      setViewing(sheet.versionId);
    })();
  };

  return (
    <CurrentSetPanel
      set={set}
      code={code}
      onRetrieve={onRetrieve}
      viewing={viewing}
      onView={onView}
      onCloseViewer={() => setViewing(null)}
    />
  );
}

/**
 * One sheet, rendered from `/download` (ADR-0061 D6). Pure, so the route it renders from can be
 * asserted in a test — D6 is violated silently, and a surface that renders from one route and cites
 * the other looks completely normal.
 */
export function SheetViewer({
  code,
  sheet,
  onClose,
}: {
  code: string;
  sheet: DrawingSheet & { nexusDocumentId: string };
  onClose?: () => void;
}) {
  const src = documentDownloadUrl(code, sheet.nexusDocumentId);
  return (
    <div className="sheet-viewer">
      <div className="sheet-viewer__bar">
        <span>
          <strong>{sheet.sheetNumber}</strong> {sheet.title}{' '}
          <span className="muted">
            · rev {sheet.revision} · {sheet.issueLabel}
          </span>
        </span>
        {onClose && (
          <button type="button" className="sheet-viewer__close" onClick={onClose}>
            Close
          </button>
        )}
      </div>
      <p className="muted sheet-viewer__source">
        Served by <code>/download</code> — the same route this sheet&rsquo;s link cites. It shows
        the document&rsquo;s <strong>current</strong> bytes, which can be newer than the revision
        this row records. If the sheet does not appear, open it from the file link above.
      </p>
      {/*
        ⚠️ WHY A FRAME TO THE API WORKS, AND WHEN IT DOES NOT. The api sends helmet's
        `X-Frame-Options: SAMEORIGIN` + `frame-ancestors 'self'`, and the Hub is another origin. Measured
        2026-09-23 in headless Chrome and Edge: those headers are NOT applied to the 302, only to the
        final response — the presigned store URL, which carries neither — so the sheet renders. But an
        ERROR from the route (404, 502, a lapsed-session 401) IS the final response, and the browser
        blocks it: the frame shows a blocked icon, not the message. Hence the fallback sentence above,
        and the session renewal before this frame mounts. Firefox and Safari were not measured.
      */}
      <iframe className="sheet-viewer__frame" title={`Sheet ${sheet.sheetNumber}`} src={src} />
    </div>
  );
}

/** The panel proper — pure, so every state can be rendered in a test. `null` means "still loading". */
/*
  `code` is threaded in so a sheet can link to its document. It is NOT derivable from the set —
  the download route is project-scoped (`/projects/:code/documents/:id/download`) and the Current
  Set carries no project code, deliberately: a pursuit has none (canon A2).
*/
export function CurrentSetPanel({
  set,
  code,
  onRetrieve,
  viewing = null,
  onView,
  onCloseViewer,
}: {
  set: CurrentSet | null;
  code: string;
  /** Renew the session, then navigate. Injected so this component stays render-only and testable. */
  onRetrieve?: (documentId: string) => void;
  /** The `versionId` of the sheet open in the viewer, if any. */
  viewing?: string | null;
  /** Open (or toggle shut) the inline viewer. Absent ⇒ no View control is rendered at all. */
  onView?: (sheet: DrawingSheet) => void;
  onCloseViewer?: () => void;
}) {
  if (!set) {
    return (
      <section className="card">
        <h2>Current Set</h2>
        <p className="muted">Loading…</p>
      </section>
    );
  }

  /*
    ⚠️ THREE STATES, NOT TWO. "Could not load" is distinct from "no drawings yet", and conflating them
    tells a reader this project has no drawings when we simply failed to ask. On a contract exhibit
    that is a wrong answer delivered confidently.
  */
  if (!set.loaded) {
    return (
      <section className="card">
        <h2>Current Set</h2>
        <p className="muted">
          {set.forbidden ? (
            <>
              The drawing record is <strong>staff-only</strong>, and this account may not read it.
              That is a permission, not a fault.
            </>
          ) : (
            <>
              The drawing record could not be loaded. This does <strong>not</strong> mean the
              project has no drawings.
            </>
          )}
        </p>
      </section>
    );
  }

  const groups = groupByDiscipline(set.sheets);
  /*
    How many sheets we could NOT match to a document. Counted from the set rather than asserted in
    prose: the old footnote said flatly that nothing was downloadable, and that stopped being true
    the moment a set had matched documents.
  */
  const unlinked = set.sheets.filter((s) => !s.nexusDocumentId).length;

  return (
    <section className="card">
      <h2>Current Set</h2>
      <p className="muted">
        The versions no other version supersedes — a query over the record, never a stored file.
      </p>

      {set.sheetCount === 0 ? (
        <p className="muted">
          No drawings on this record yet.
          {set.supersededCount > 0 ? (
            <>
              {' '}
              ⚠️ {set.supersededCount} superseded version(s) exist, so the record is not empty —
              every sheet it holds has been replaced.
            </>
          ) : null}
        </p>
      ) : (
        <>
          <p>
            <strong>{set.sheetCount}</strong> sheet{set.sheetCount === 1 ? '' : 's'} across{' '}
            <strong>{groups.length}</strong> discipline{groups.length === 1 ? '' : 's'}
            {set.supersededCount > 0 ? <> · {set.supersededCount} superseded</> : null}
            {set.retiredCount > 0 ? <> · {set.retiredCount} retired by a renumbered set</> : null}
          </p>
          {groups.map(([discipline, sheets]) => (
            <div key={discipline} className="drawing-discipline">
              <h3>
                {discipline} <span className="muted">({sheets.length})</span>
              </h3>
              <table>
                <thead>
                  <tr>
                    <th>Sheet</th>
                    <th>Title</th>
                    <th>Revision</th>
                    <th>Issue</th>
                    <th>File</th>
                  </tr>
                </thead>
                <tbody>
                  {sheets.map((s) => (
                    <Fragment key={s.versionId}>
                      <tr>
                        <td>{s.sheetNumber}</td>
                        <td>{s.title}</td>
                        <td>{s.revision}</td>
                        <td>
                          {s.issueLabel} <span className="muted">({s.issueDate})</span>
                        </td>
                        {/*
                        🔴 A LINK ONLY WHEN WE HOLD THE DOCUMENT ID. A sheet we could not match
                        renders as plain text with a reason — never a control that opens the wrong
                        drawing, and never a dead one. In this domain the difference between two
                        candidate documents is the difference between what a subcontractor bid and
                        what they built.

                        ⚠️ `/download` and NOT `/view` (ADR-0061 D6). Nexus serves DIFFERENT BYTES
                        from the two — `/view` resolves without the content-type tiebreak, so it can
                        return a shadow rendition where `/download` returns the source. D6 rules that
                        one route supplies both the pixels and the citation for a surface, and that
                        WHICH route it is, is recorded. This is that record.
                      */}
                        <td>
                          {s.nexusDocumentId ? (
                            /*
                            🔴 `documentDownloadUrl`, NEVER A HAND-BUILT PATH. It prefixes
                            `apiBaseUrl` and carries no `/api` segment — the Hub and the api are
                            DIFFERENT ORIGINS, so a root-relative href stays on the Hub host and
                            never reaches the route at all. Caught by Bugbot; CI was green, because
                            no test can see a wrong origin in static markup.

                            ⚠️ A real href AND a click handler, exactly as `DocumentHits` and the
                            documents panel do. The href makes middle-click and "copy link address"
                            behave; the handler takes the ordinary click only to renew the session
                            first, because this route is a TOP-LEVEL NAVIGATION — the SPA never
                            refreshes the 15-minute access cookie on the way out, so a lapsed one
                            returns raw 401 JSON instead of the file.

                            Their comment states the rule this now follows: "two ways to open a file
                            that behave differently is a way to open a file wrongly."
                          */
                            <a
                              href={documentDownloadUrl(code, s.nexusDocumentId)}
                              onClick={(e) => {
                                if (!onRetrieve || !s.nexusDocumentId) return;
                                e.preventDefault();
                                onRetrieve(s.nexusDocumentId);
                              }}
                            >
                              {s.fileName}
                            </a>
                          ) : (
                            <span
                              className="muted"
                              title="This file was not matched to a document in this project — either it was never ingested here, or its name matched more than one document and we refuse to guess."
                            >
                              {s.fileName} <em>(not linked)</em>
                            </span>
                          )}
                          {/* A View control only for a MATCHED sheet — the same refusal as the link. */}
                          {s.nexusDocumentId && onView && (
                            <>
                              {' '}
                              <button
                                type="button"
                                className="sheet-view-toggle"
                                aria-expanded={viewing === s.versionId}
                                onClick={() => onView(s)}
                              >
                                {viewing === s.versionId ? 'Hide' : 'View'}
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                      {viewing === s.versionId && s.nexusDocumentId && (
                        <tr className="sheet-viewer-row">
                          <td colSpan={5}>
                            <SheetViewer
                              code={code}
                              sheet={{ ...s, nexusDocumentId: s.nexusDocumentId }}
                              {...(onCloseViewer ? { onClose: onCloseViewer } : {})}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          {/*
            ⚠️ COUNTED, not asserted. The old note said flatly that nothing was downloadable, which
            stopped being true the moment a set had matched documents — and a standing sentence that
            quietly goes stale is the failure this repo keeps paying for. This says what is actually
            true of the set on screen.
          */}
          {unlinked > 0 && (
            <p className="muted">
              {unlinked} of {set.sheets.length} sheet{set.sheets.length === 1 ? '' : 's'} could not
              be matched to a document in this project — either the file was never ingested here, or
              its name matched more than one document, and the record refuses to guess which.
            </p>
          )}
          <p className="muted">
            A link opens the CURRENT bytes of that document, not the exact revision this row
            records.
          </p>
        </>
      )}
    </section>
  );
}

/**
 * THE SET AS FILED — ADR-0061 D3: a set is a query "as of" an issue, never a stored file.
 *
 * 🔴 "FILED", NEVER "ISSUED" (Brad, 2026-09-24). The record does not state which issue came before
 * which — canon's date is text D2 forbids us parsing — so the order is the record's own filing order.
 * The surface says so, because "as the record stood after X was filed" and "as of X's issue date" are
 * different claims the moment an older issue is filed late.
 *
 * 🔴 ITS LINKS OPEN A SHEET AS FILED, NEVER THROUGH `/download` (ADR-0061 D4). `/download` serves a
 * document's NEWEST file, so a link on a superseded sheet would open the drawing that replaced it — the
 * difference between what a subcontractor bid and what they built. Each link goes to the as-filed route,
 * which serves the version that stood when the sheet's issue was filed, or says in words why it cannot.
 * Built before Nexus's retention hold (Brad, 2026-10-14), so a cited version can be purged; the panel
 * says so up front.
 */
export function DrawingSetAsFiled({ code }: { code: string }) {
  const [issues, setIssues] = useState<
    { issues: FiledIssue[]; forbidden: boolean } | null | 'loading'
  >('loading');
  const [selected, setSelected] = useState<string | null>(null);
  const [set, setSet] = useState<SetAsFiled | null>(null);

  useEffect(() => {
    let live = true;
    void getFiledIssues(code).then((r) => {
      if (live) setIssues(r);
    });
    return () => {
      live = false;
    };
  }, [code]);

  useEffect(() => {
    if (!selected) return;
    let live = true;
    setSet(null);
    void getSetAsFiled(code, selected).then((r) => {
      if (live) setSet(r);
    });
    return () => {
      live = false;
    };
  }, [code, selected]);

  // The same open-a-tab-then-renew order as every other retrieval (`retrieveUrl`), and the same
  // sign-in routing when the session is gone.
  const navigate = useNavigate();
  const onOpen = (versionId: string) => {
    void (async () => {
      const outcome = await retrieveUrl(
        {
          openTab: () => window.open('', '_blank'),
          ensureSession: async () => (await getSession()) !== null,
          goTo: (url) => window.location.assign(url),
        },
        sheetAsFiledUrl(code, versionId),
      );
      if (outcome === 'signed-out')
        void navigate(signInPathFor(window.location, window.location.origin), { replace: true });
    })();
  };

  return (
    <SetAsFiledPanel
      code={code}
      issues={issues}
      selected={selected}
      set={set}
      onSelect={setSelected}
      onOpen={onOpen}
    />
  );
}

/** Render-only, so every state is assertable under SSR (the same split as `ProjectDrawings`). */
export function SetAsFiledPanel({
  code,
  issues,
  selected,
  set,
  onSelect,
  onOpen,
}: {
  code: string;
  issues: { issues: FiledIssue[]; forbidden: boolean } | null | 'loading';
  selected: string | null;
  set: SetAsFiled | null;
  onSelect?: (issueId: string) => void;
  /** Renew the session, then open the sheet as filed. Injected so this stays render-only. */
  onOpen?: (versionId: string) => void;
}) {
  // Staff-only, like the Current Set: a viewer already sees the permission note there.
  if (issues !== 'loading' && issues?.forbidden) return null;
  return (
    <section className="card">
      <h2>The set as filed</h2>
      <p className="muted">
        The Current Set as this record stood after a chosen issue was <strong>filed</strong> — in
        the order the record filed its issues, which is not necessarily the order the drawings were
        issued. A file link opens the sheet <strong>as filed</strong> — the version of its file that
        stood when its issue was filed — never a newer one. The document store can still trash or
        delete a file; when it has, the link says so instead of opening another one.
      </p>
      {issues === 'loading' ? (
        <p className="muted">Loading…</p>
      ) : issues === null ? (
        <p className="muted">
          The list of filed issues could not be loaded. This does <strong>not</strong> mean the
          record has none.
        </p>
      ) : issues.issues.length === 0 ? (
        <p className="muted">No issues have been filed on this record yet.</p>
      ) : (
        <>
          <label>
            As the record stood after{' '}
            <select
              value={selected ?? ''}
              onChange={(e) => {
                if (e.target.value) onSelect?.(e.target.value);
              }}
            >
              <option value="">choose an issue…</option>
              {issues.issues.map((i, n) => (
                <option key={i.id} value={i.id}>
                  {n + 1}. {i.label} (dated {i.issueDate}, filed {timestamp(i.filedAt)})
                  {issueMarks(i)}
                </option>
              ))}
            </select>{' '}
            was filed
          </label>
          {selected && <AsOfSheets code={code} set={set} {...(onOpen ? { onOpen } : {})} />}
        </>
      )}
    </section>
  );
}

function AsOfSheets({
  code,
  set,
  onOpen,
}: {
  code: string;
  set: SetAsFiled | null;
  onOpen?: (versionId: string) => void;
}) {
  if (!set) return <p className="muted">Loading…</p>;
  if (!set.loaded) {
    return (
      <p className="muted">
        That set could not be loaded. This does <strong>not</strong> mean it was empty.
      </p>
    );
  }
  if (set.sheets.length === 0) return <p className="muted">No sheets had been filed by then.</p>;
  return (
    <>
      <p className="muted">{set.sheets.length} sheets.</p>
      {groupByDiscipline<AsOfSheet>(set.sheets).map(([discipline, sheets]) => (
        <Fragment key={discipline}>
          <h3 className="card__title">{discipline}</h3>
          <ul className="sheetlist">
            {sheets.map((s) => (
              <li key={s.versionId}>
                <strong>{s.sheetNumber}</strong> {s.title} — rev {s.revision} ({s.issueLabel}) —{' '}
                {s.linked ? (
                  /*
                    A real href (middle-click, copy link) AND a click handler that renews the session
                    first — the same pairing as the Current Set's links. The href is the AS-FILED
                    route, built by `sheetAsFiledUrl`; never `/download`, which serves the newest file.
                  */
                  <a
                    href={sheetAsFiledUrl(code, s.versionId)}
                    onClick={(e) => {
                      if (!onOpen) return;
                      e.preventDefault();
                      onOpen(s.versionId);
                    }}
                  >
                    {s.fileName}
                  </a>
                ) : (
                  <span
                    className="muted"
                    title="This file was not matched to a document in this project — either it was never ingested here, or its name matched more than one document and we refuse to guess."
                  >
                    {s.fileName} <em>(not linked)</em>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Fragment>
      ))}
    </>
  );
}
